import { useCallback, useContext } from 'react';

import { BreakpointContext } from './BreakpointProvider';
import type {
  Breakpoint,
  BreakpointRange,
  ResponsiveProp,
} from './breakpoints';
import { breakpointKeys } from './breakpoints';
import { getResponsiveProp } from './useResponsiveProp';

/**
 * Access breakpoint context for managing responsive ranges.
 *
 * The returned function takes a breakpoint range as its only argument and
 * returns `true` if the current matched breakpoints fall within the specified
 * range, otherwise `false`.
 */
export function useResponsiveRange() {
  const matchedBreakpoints = useContext(BreakpointContext);

  return useCallback(
    // Prefer declaration over lambda in this case. Retain the function name in
    // development for improved debugging.
    function responsiveRange(range: BreakpointRange) {
      return getResponsiveRange(range, matchedBreakpoints);
    },
    [matchedBreakpoints]
  );
}

/** @private Exported for internal use. */
export function getResponsiveRange(
  range: BreakpointRange,
  matchedBreakpoints: Breakpoint[]
): boolean {
  const startIndex =
    'above' in range ? breakpointKeys.indexOf(range.above) + 1 : 0;
  const endIndex =
    'below' in range
      ? breakpointKeys.indexOf(range.below) - 1
      : breakpointKeys.length - 1;

  const prop: ResponsiveProp<boolean> = Object.fromEntries(
    breakpointKeys.map((key, index) => {
      return [key, index >= startIndex && index <= endIndex];
    })
  );

  return !!getResponsiveProp(prop, matchedBreakpoints);
}
